import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { settingsRepository } from '@/data/settings/repositories/settings_repository_impl'
import { getAxiosLikeErrorMessage } from '@/services/apiErrors'
import { logError } from '@/utils/logger'
import type { TimezoneFormErrors } from '../models/settings_ui_state'

export function useTimezoneSettingsViewModel() {
  const navigate = useNavigate()
  const [timezone, setTimezone] = useState('')
  const [errors, setErrors] = useState<TimezoneFormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      try {
        const data = await settingsRepository.getTimezone()
        if (!cancelled) setTimezone(data.timezone)
      } catch (error) {
        logError('Failed to load timezone:', error)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setIsSubmitting(true)

      const newErrors: TimezoneFormErrors = {}
      if (!timezone || timezone === '') {
        newErrors.timezone = 'Timezone is required'
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors)
        setIsSubmitting(false)
        return
      }

      try {
        await settingsRepository.saveTimezone({ timezone })
        alert('Timezone saved successfully!')
      } catch (error: unknown) {
        logError('Failed to save timezone:', error)
        alert(getAxiosLikeErrorMessage(error, 'Failed to save timezone. Please try again.'))
      } finally {
        setIsSubmitting(false)
      }
    },
    [timezone]
  )

  const handleCancel = useCallback(() => navigate('/admin'), [navigate])

  return {
    timezone,
    errors,
    isSubmitting,
    isLoading,
    setTimezone,
    setErrors,
    handleSubmit,
    handleCancel,
  }
}
